<script setup lang="ts">
import { Ref, ref, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import { addFolder } from './api.ts'
import { useLibraryStore } from './library.store.ts'
import { useUserStore } from './../authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import {LibraryFolder } from './library_types.ts'
import NewFolderInput from '@/components/NewFolderInput.vue'
import { useContentAccessStore } from '@/content_access/content_access.store'

const newFolderName = ref('')
const props = defineProps({
  externalOpen: {
    type: Boolean,
    default: false
  },
  existingFolders: {
    type: Array,
    required: false
  }
})
const emit = defineEmits(['update:externalOpen'])
const userStore = useUserStore()
const libraryStore = useLibraryStore()
const contentAccessStore = useContentAccessStore()
const analytics = getAnalyticsTracker()
const { folders, folderCount } = storeToRefs(libraryStore)
const { maxFoldersReached, libraryQuotas } = storeToRefs(contentAccessStore)

const existingFolderNames = computed(() => {
  return folders.value.map((item: { name: string }) => {
    return item.name.toLowerCase()
  })
})

const isNewFolder = computed(() => {
  if (newFolderName.value) {
    return !existingFolderNames.value.includes(newFolderName.value.toLowerCase())
  } else {
    return false
  }
})

const visibleRef: Ref<boolean> = ref(props.externalOpen)
const createError: Ref<boolean> = ref(false)
const isCreating: Ref<boolean> = ref(false)

const closeModal = function (): void {
  newFolderName.value = ''
  visibleRef.value = false
  emit('update:externalOpen', false)
}

watch(
  () => props.externalOpen,
  () => {
    visibleRef.value = props.externalOpen
  }
)

watch(
  () => visibleRef.value,
  () => {
    emit('update:externalOpen', visibleRef.value)
  }
)

const disableSubmit = computed(() => {
  return newFolderName.value === '' || isNewFolder.value === false
})

function handleFolderQuotaExceeded() {
  analytics.sendEvent('Content Access Blocked', {
    blocked_action: 'Add Folder',
    blocking_quota: 'max_folders',
    blocking_count: folderCount.value,
    blocking_quota_value: libraryQuotas.value.maxFolders
  })
}

const createFolder = async function () {
  createError.value = false
  if (!disableSubmit.value && !isCreating.value) {
    isCreating.value = true
    try {
      if (maxFoldersReached.value) {
        handleFolderQuotaExceeded()
        isCreating.value = false
        return
      }
      const data = await addFolder(newFolderName.value, userStore.userId) as LibraryFolder
      if (!data?.uuid) {
        createError.value = true
        console.error('Failed to create folder:', data)
        return
      }
      analytics.sendEvent('Added New Folder')
      analytics.sendEvent('Added New Student') // to be deprecated
      createError.value = false
      await libraryStore.fetchFolders(userStore.userId)
      closeModal()
    } catch (e) {
      createError.value = true
      console.error(e)
    } finally {
      isCreating.value = false
    }
  }
}
</script>
<template>
  <GbDialog
    dialogId="new-folder-modal"
    v-model:visible="visibleRef"
    header="New Folder"
    :footer-buttons="true"
    @hide="closeModal"
  >
    <template #bodyContent>
      <NewFolderInput
        v-model="newFolderName"
        :isDuplicate="!isNewFolder"
        :errorMessage="createError ? 'We couldn\'t create the folder. Try again later.' : ''"
        @enter="createFolder"
        :maxReached="maxFoldersReached"
        :isLoading="isCreating"
      />
    </template>
    <template #footerButtonLeft>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        @click="closeModal"
      ></GbButton>
    </template>
    <template #footerButtonRight>
      <GbButton
        type="button"
        gbTheme="bluePrimary"
        label="Create"
        @click="createFolder"
        :disabled="disableSubmit"
        :aria-disabled="disableSubmit"
      ></GbButton>
    </template>
  </GbDialog>
</template>
